import { Card } from '@citizen-walk/shared-ui';
import { useParams } from 'react-router-dom';
import { useAdminAuth } from '../auth';
import { DistrictAiAnalysisPanel } from './DistrictAiAnalysisPanel';
import { DistrictAiDailyQuotaPanel } from './DistrictAiDailyQuotaPanel';
import { DistrictPostAnalysesPanel } from './DistrictPostAnalysesPanel';
import { DistrictAiDialoguePanel } from './DistrictAiDialoguePanel';
import { DistrictAiVerificationPanel } from './DistrictAiVerificationPanel';
import { DistrictForceReanalyzePanel } from './DistrictForceReanalyzePanel';
import { DistrictStreetViewPanel } from './DistrictStreetViewPanel';
import { DistrictSubPageHeader } from './DistrictSubPageHeader';
import { useDistrict } from './useDistricts';

/**
 * Sprint BG-Menu (第 3 段、DistrictDetailPage 分割 + AI 設定統合):
 * `/districts/:id/ai` に AI 関連 3 panel を集約。
 *
 *   - DistrictAiDialoguePanel (対話モード prompt 設定)
 *   - DistrictAiVerificationPanel (検証パイプライン設定)
 *   - DistrictForceReanalyzePanel (master only、強制再解析)
 *
 * 冒頭に「この地区で利用する AI の振る舞いを 3 種類設定します」概要説明を表示。
 */
export function DistrictAiSettingsPage() {
  const { districtId } = useParams<{ districtId: string }>();
  const state = useDistrict(districtId ?? null);
  const { state: authState } = useAdminAuth();

  const isMaster = authState.role === 'master';
  const uid = authState.user?.uid ?? '';

  if (state.status === 'loading') {
    return (
      <Card padding="md">
        <p className="text-sm text-slate-600">読込中…</p>
      </Card>
    );
  }
  if (state.status === 'not-found') {
    return (
      <Card padding="md">
        <p className="text-sm text-slate-700">
          地区が見つかりません (id: <code>{districtId}</code>)。
        </p>
      </Card>
    );
  }
  if (state.status === 'error') {
    return (
      <Card padding="md" className="border-danger-600">
        <p className="text-sm text-danger-700">読込エラー: {state.error.message}</p>
      </Card>
    );
  }

  return (
    <>
      <DistrictSubPageHeader
        districtId={state.district.id}
        districtName={state.district.name}
        activeTo={`/districts/${state.district.id}/ai`}
      />
      <div className="grid grid-cols-1 gap-4">
        <Card padding="md">
          <h2 className="text-base font-bold text-slate-900 mb-1">AI 設定</h2>
          <p className="text-xs text-slate-600">
            この地区で利用する AI の振る舞いを 4 種類設定します: 投稿の<strong>AI解析設定</strong>
            、投稿時の<strong>対話モード</strong>、AI 解析結果の<strong>検証パイプライン</strong>
            、必要に応じて
            <strong>強制再解析</strong> (master のみ)。
          </p>
        </Card>

        <DistrictAiAnalysisPanel district={state.district} uid={uid} canEdit={isMaster} />
        <DistrictPostAnalysesPanel district={state.district} uid={uid} canEdit={isMaster} />
        <DistrictAiDailyQuotaPanel district={state.district} uid={uid} canEdit={isMaster} />
        <DistrictAiDialoguePanel district={state.district} uid={uid} canEdit={isMaster} />
        <DistrictAiVerificationPanel district={state.district} uid={uid} canEdit={isMaster} />
        {isMaster && <DistrictForceReanalyzePanel district={state.district} isMaster={isMaster} />}
        <DistrictStreetViewPanel district={state.district} uid={uid} canEdit={isMaster} />
      </div>
    </>
  );
}
